// 홈 상단 카드(2026-10-19 · 시안 A안 docs/design/home-card-send-variants.html) — 어드민 「알림 관리 › 카드 발송」으로 받은 카드.
//   크리에이터 홈(Home.jsx · 네이버 클립 수익 카드 아래) · 권리사 작품 화면(VideoRightsLayout) · 파트너 홈(PartnerLayout) 맨 위.
//   한 번에 2장까지 펼치고 나머지는 「카드 N개 더 보기」. 「확인했습니다」를 누르면 그 카드는 다시 안 뜬다(home_card_ack confirm).
//   링크 버튼은 누른 기록(click)만 남기고 카드는 그대로 둔다. 다른 회원으로 보기 중에는 읽기만(서버도 seen 을 찍지 않는다).
//   규칙 src/lib/homeCards.js · 호출 src/lib/homeCardsApi.js · 문서 docs/pages/main/Home.md 「홈 상단 카드」.
import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Badge, Flex, Text, useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { tt } from "../../i18n/tt";
import { isImpersonating } from "../../lib/policyConsentRules";
import { cardUntilLabel, normalizeCardLink, splitVisibleCards } from "../../lib/homeCards";
import { ackHomeCard, fetchMyHomeCards } from "../../lib/homeCardsApi";

const TONE = {
  brand: { bg: "brand.50", border: "brand.200", chip: "brand.500", title: "brand.700" },
  warn: { bg: "accent.50", border: "accent.200", chip: "status.warning", title: "accent.700" },
  plain: { bg: "white", border: "neutral.200", chip: "brand.500", title: "neutral.800" },
};

/** 카드 한 장 — 어드민 「카드 발송」 미리보기도 같은 컴포넌트를 쓴다(보이는 모습 = 실제 모습) */
export function HomeCard({ card, readOnly, busy, onConfirm, onOpenLink }) {
  const t = TONE[card.tone] || TONE.brand;
  const link = normalizeCardLink(card.link);
  const until = cardUntilLabel(card.ends_at);
  return (
    <Box
      bg={t.bg}
      border="1px solid"
      borderColor={t.border}
      borderRadius="8px"
      boxShadow="sm"
      px={{ base: 3, md: 4 }}
      py={3}
      data-home-card={card.id}
    >
      <Flex align="center" gap={2} mb={1} minW={0}>
        <Badge bg={t.chip} color="white" fontSize="10px" fontWeight="800" px={1.5} py={0.5} borderRadius="4px" textTransform="none" whiteSpace="nowrap">
          {tt("a.homeCard.badge", "운영팀 안내")}
        </Badge>
        <Text fontSize="11px" color="neutral.500" noOfLines={1} minW={0}>
          {until ? tt("a.homeCard.fromUntil", "레이블리 운영팀 · {{v0}}까지", { v0: until }) : tt("a.homeCard.from", "레이블리 운영팀")}
        </Text>
      </Flex>
      <Flex direction={{ base: "column", md: "row" }} align={{ base: "stretch", md: "center" }} gap={{ base: 2, md: 4 }}>
        <Box flex={1} minW={0}>
          <Text fontSize={{ base: "sm", md: "15px" }} fontWeight="800" color={t.title} lineHeight="1.4">
            {card.title}
          </Text>
          <Text fontSize="xs" color="neutral.600" mt={1} lineHeight="1.55" whiteSpace="pre-line">
            {card.body}
          </Text>
        </Box>
        <Flex gap={2} flexShrink={0} direction={{ base: "column-reverse", md: "row" }} w={{ base: "100%", md: "auto" }}>
          <Button
            size="sm"
            variant="outline"
            colorScheme="brand"
            bg="white"
            w={{ base: "100%", md: "auto" }}
            isLoading={busy}
            isDisabled={readOnly}
            title={readOnly ? tt("a.homeCard.readOnly", "다른 회원으로 보는 중에는 누를 수 없어요") : undefined}
            onClick={() => onConfirm(card)}
          >
            {tt("a.homeCard.confirm", "확인했습니다")}
          </Button>
          {link.href && (
            <Button size="sm" colorScheme="brand" w={{ base: "100%", md: "auto" }} onClick={() => onOpenLink(card, link)}>
              {card.cta_label || tt("a.homeCard.cta", "자세히 보기")}
            </Button>
          )}
        </Flex>
      </Flex>
    </Box>
  );
}

export default function HomeCardStack({ email, mb = 4 }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const readOnly = isImpersonating();

  const load = useCallback(async () => {
    if (!email) { setCards([]); return; }
    try {
      setCards(await fetchMyHomeCards(email));
    } catch (_) {
      setCards([]); // 카드는 덤 — 못 읽으면 아무것도 그리지 않는다(첫 화면을 막지 않는다)
    }
  }, [email]);
  useEffect(() => { load(); }, [load]);

  const onConfirm = async (card) => {
    if (readOnly) return;
    setBusyId(card.id);
    try {
      await ackHomeCard(card.id, "confirm");
      setCards((list) => list.filter((c) => c.id !== card.id));
    } catch (e) {
      toast({ title: tt("a.homeCard.confirmFail", "확인 처리를 못 했어요. 잠시 뒤 다시 눌러 주세요."), status: "error", duration: 3000 });
    } finally {
      setBusyId(null);
    }
  };

  const onOpenLink = (card, link) => {
    if (!readOnly) ackHomeCard(card.id, "click").catch(() => {});
    if (link.kind === "internal") navigate(link.href);
    else if (link.kind === "external") window.open(link.href, "_blank", "noopener,noreferrer");
  };

  if (!cards.length) return null;
  const { shown, hidden } = splitVisibleCards(cards, expanded);
  return (
    <Flex direction="column" gap={2} mb={mb} data-home-card-stack>
      {shown.map((c) => (
        <HomeCard key={c.id} card={c} readOnly={readOnly} busy={busyId === c.id} onConfirm={onConfirm} onOpenLink={onOpenLink} />
      ))}
      {(hidden > 0 || (expanded && cards.length > 2)) && (
        <Button size="xs" variant="ghost" colorScheme="brand" alignSelf="center" onClick={() => setExpanded((v) => !v)}>
          {hidden > 0 ? tt("a.homeCard.more", "카드 {{v0}}개 더 보기", { v0: hidden }) : tt("a.homeCard.less", "접기")}
        </Button>
      )}
    </Flex>
  );
}
